import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CAMPOS = [["clave", "Clave (ej. isr_exencion_casa_habitacion)"], ["concepto", "Concepto"], ["valor", "Valor"],
  ["fuente", "Fuente oficial"], ["fuente_url", "Enlace a la fuente (https://…)"], ["vigente_desde", "Vigente desde (AAAA-MM-DD)"], ["notas", "Notas"]];

const UmbralDialog = ({ item, unidades, onClose, onSaved }) => {
  const [f, setF] = useState({});
  useEffect(() => { setF(item?.clave ? item : { unidad: "UMA" }); }, [item]);
  const save = async () => {
    try {
      const body = { ...f, valor: Number(f.valor) };
      const { data } = item?.clave ? await api.put(`/copiloto/umbrales/${item.clave}`, body) : await api.post("/copiloto/umbrales", body);
      onSaved(data); onClose(); toast.success("Umbral guardado");
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo guardar"); }
  };
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="umbral-dialog">
        <DialogHeader><DialogTitle>{item?.clave ? "Editar umbral" : "Nuevo umbral"}</DialogTitle></DialogHeader>
        {CAMPOS.map(([k, l]) => (
          <label key={k} className="block text-sm font-semibold text-slate-700">{l}
            <input value={f[k] ?? ""} disabled={k === "clave" && !!item?.clave} onChange={(e) => setF({ ...f, [k]: e.target.value })}
              data-testid={`umbral-${k}`} className="mt-1 w-full h-10 rounded-lg border border-[#CBD5E1] px-2 text-sm font-normal" />
          </label>
        ))}
        <label className="block text-sm font-semibold text-slate-700">Unidad original
          <Select value={f.unidad || "UMA"} onValueChange={(v) => setF({ ...f, unidad: v })}>
            <SelectTrigger className="mt-1" data-testid="umbral-unidad"><SelectValue /></SelectTrigger>
            <SelectContent>{unidades.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
          </Select>
        </label>
        <DialogFooter><Button onClick={save} data-testid="umbral-save">Guardar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const UmbralesPanel = ({ meta }) => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const isOwner = meta.permisos.puede_publicar;
  const load = useCallback(async () => { setItems((await api.get("/copiloto/umbrales")).data); }, []);
  useEffect(() => { load(); }, [load]);
  const del = async (clave) => {
    if (!window.confirm("¿Borrar este umbral?")) return;
    await api.delete(`/copiloto/umbrales/${clave}`); load();
  };
  return (
    <div>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-slate-600 max-w-2xl">Montos en su unidad original (UMA, UDI o pesos) con la fuente oficial. {isOwner ? "Solo tú puedes editarlos." : "Solo el propietario puede editarlos."}</p>
        {isOwner && <Button onClick={() => setEditing({})} className="bg-[#1E3A5F] hover:bg-[#16304F]" data-testid="umbral-new"><Plus className="w-4 h-4 mr-1" /> Nuevo umbral</Button>}
      </div>
      <ul className="mt-5 rounded-2xl bg-white border border-[#E2DCD2] divide-y divide-[#E2DCD2]" data-testid="umbrales-list">
        {!items.length && <li className="p-8 text-center text-slate-500">Aún no hay umbrales capturados.</li>}
        {items.map((u) => (
          <li key={u.clave} className="p-4 flex flex-wrap items-start justify-between gap-3" data-testid={`umbral-row-${u.clave}`}>
            <div>
              <div className="font-semibold text-slate-900">{u.concepto}</div>
              <div className="text-lg font-heading font-extrabold text-[#1E3A5F]">{Number(u.valor).toLocaleString("es-MX")} {u.unidad}</div>
              <div className="text-xs text-slate-500 mt-1">
                {u.fuente}{u.fuente_url && <a href={u.fuente_url} target="_blank" rel="noopener noreferrer" className="ml-1 inline-flex items-center text-[#1E3A5F]"><ExternalLink className="w-3 h-3" /></a>}
                {u.vigente_desde && ` · desde ${u.vigente_desde}`} · {u.clave}
              </div>
            </div>
            {isOwner && (
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setEditing(u)} data-testid={`umbral-edit-${u.clave}`}>Editar</Button>
                <Button size="sm" variant="outline" onClick={() => del(u.clave)} data-testid={`umbral-delete-${u.clave}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            )}
          </li>
        ))}
      </ul>
      <UmbralDialog item={editing} unidades={meta.unidades} onClose={() => setEditing(null)} onSaved={load} />
    </div>
  );
};
